import React, { useEffect, useState, useMemo, useCallback } from "react";
import Moment from "react-moment";
import CandidatePay from "./CandidatePay";
import PurchaseOrders from "./PurchaseOrders";
import AddNewTimeLog from "./AddNewTimeLog";
import UploadInvoice from "./UploadInvoice";
import AddEditNotes from "./AddEditNotes";
import {
  formatTotalHours,
  getAllMonths, getAllYears, getFieldData, getFormatedNumber,
  getMonthLastDay, getMonthName, getTotalDays, getTotalHours
} from "../../Services/appService";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useLoadTimeLogsQuery,
  useLoadTimeLogInvoiceQuery,
  useLoadTimeLogDurationQuery,
  useLoadAllRatesQuery,
  useTimeLogInvDocDeleteReqMutation
} from "../../app/store/timeLogApiSlice";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import useDocDownload from "../../app/hooks/useDocDownload";
import { getAlert, getConfirmAlert } from "../../Services/appAlerts";

const getInitials = (firstName, lastName) => {
  const f = firstName ? firstName.trim()[0] : "";
  const l = lastName ? lastName.trim()[0] : "";
  return (f + l).toUpperCase() || "??";
};

const DOC_ICON_BY_EXTENSION = {
  pdf: "fa-file-pdf-o",
  doc: "fa-file-word-o", docx: "fa-file-word-o", rtf: "fa-file-word-o", odt: "fa-file-word-o",
  xls: "fa-file-excel-o", xlsx: "fa-file-excel-o", csv: "fa-file-excel-o", ods: "fa-file-excel-o",
  ppt: "fa-file-powerpoint-o", pptx: "fa-file-powerpoint-o", odp: "fa-file-powerpoint-o",
  png: "fa-file-image-o", jpg: "fa-file-image-o", jpeg: "fa-file-image-o", gif: "fa-file-image-o",
  bmp: "fa-file-image-o", webp: "fa-file-image-o", svg: "fa-file-image-o",
  tif: "fa-file-image-o", tiff: "fa-file-image-o", heic: "fa-file-image-o",
  zip: "fa-file-archive-o", rar: "fa-file-archive-o", "7z": "fa-file-archive-o",
  tar: "fa-file-archive-o", gz: "fa-file-archive-o",
  txt: "fa-file-text-o", log: "fa-file-text-o", md: "fa-file-text-o",
  mp3: "fa-file-audio-o", wav: "fa-file-audio-o", m4a: "fa-file-audio-o",
  mp4: "fa-file-video-o", mov: "fa-file-video-o", avi: "fa-file-video-o", mkv: "fa-file-video-o",
  html: "fa-file-code-o", xml: "fa-file-code-o", json: "fa-file-code-o"
};

const getDocumentIcon = (doc) => {
  const name = String(doc?.docName || doc?.documentName || doc || "").trim().toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  if (DOC_ICON_BY_EXTENSION[ext]) return DOC_ICON_BY_EXTENSION[ext];

  const mime = String(doc?.docType || "").trim().toLowerCase();
  if (mime.startsWith("image/")) return "fa-file-image-o";
  if (mime.startsWith("audio/")) return "fa-file-audio-o";
  if (mime.startsWith("video/")) return "fa-file-video-o";
  if (mime.startsWith("text/")) return "fa-file-text-o";
  if (mime === "application/pdf") return "fa-file-pdf-o";
  if (mime.includes("word")) return "fa-file-word-o";
  if (mime.includes("sheet") || mime.includes("excel")) return "fa-file-excel-o";
  if (mime.includes("presentation") || mime.includes("powerpoint")) return "fa-file-powerpoint-o";
  if (mime.includes("zip") || mime.includes("compressed")) return "fa-file-archive-o";

  return "fa-file-text-o";
};

const getDocumentIconStyle = (doc) => {
  const icon = getDocumentIcon(doc);
  if (icon === "fa-file-pdf-o") return { color: "#dc2626" };
  if (icon === "fa-file-word-o") return { color: "#2563eb" };
  if (icon === "fa-file-excel-o") return { color: "#16a34a" };
  if (icon === "fa-file-image-o") return { color: "#9333ea" };
  if (icon === "fa-file-powerpoint-o") return { color: "#ea580c" };
  if (icon === "fa-file-archive-o") return { color: "#d97706" };
  return { color: "#2563eb" };
};

const CANDIDATE_INVOICE_STYLES = {
  pageActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "4px 0 12px 0"
  },
  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  card: {
    borderRadius: "0px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    overflow: "hidden"
  },
  cardHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid #e2e8f0",
    background: "#ffffff"
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    border: "1px solid #e2e8f0",
    boxShadow: "none",
    flexShrink: 0
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#0f172a",
    margin: 0
  },
  subtitle: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#64748b",
    margin: "2px 0 0 0",
    fontWeight: 600
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer"
  },
  cardBody: {
    padding: "0"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "14px",
    padding: "20px 22px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: "0px",
    padding: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#64748b",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#0f172a",
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: 1
  },
  statHint: {
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#64748b",
    marginTop: "6px"
  },
  utilityRow: {
    padding: "0 22px 14px 22px"
  },
  utilityPanel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
    padding: "14px 18px",
    borderRadius: "0px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    width: "100%",
    flexWrap: "wrap"
  },
  utilityPanelTop: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: 1,
    minWidth: 0
  },
  utilityIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    border: "1px solid #e2e8f0",
    flexShrink: 0
  },
  utilityCopy: {
    minWidth: 0,
    flex: 1
  },
  utilityLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#64748b",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    margin: 0
  },
  utilityValue: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#0f172a",
    lineHeight: 1.2,
    marginTop: "2px"
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
    padding: "0 22px 18px 22px"
  },
  utilityFilters: {
    display: "flex",
    alignItems: "flex-end",
    gap: "12px",
    flexWrap: "wrap"
  },
  utilityFilterField: {
    minWidth: "150px"
  },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "6px",
    display: "block"
  },
  inputShell: {
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    height: "40px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#0f172a",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)"
  },
  summaryTableWrap: {
    border: "1px solid #e2e8f0",
    borderRadius: "0px",
    overflow: "hidden",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    marginBottom: "20px"
  },
  tableWrap: {
    overflowX: "auto",
    backgroundColor: "#ffffff"
  },
  table: {
    marginBottom: 0,
    minWidth: "1120px"
  },
  candidateSummaryTable: {
    width: "100%",
    marginBottom: 0,
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: "13px",
    color: "#334155"
  },
  tableHeadCell: {
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    padding: "14px 16px",
    whiteSpace: "nowrap",
    verticalAlign: "top"
  },
  tableHeadSubtext: {
    display: "block",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "none",
    color: "#94a3b8",
    marginTop: "4px"
  },
  cell: {
    verticalAlign: "top",
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "13px",
    color: "#334155",
    fontWeight: 600
  },
  candidateSummaryCell: {
    verticalAlign: "top",
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
    background: "#ffffff",
    fontWeight: 600
  },
  idPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "0",
    borderRadius: "0px",
    backgroundColor: "transparent",
    border: "none",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 600
  },
  secondaryPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "0",
    borderRadius: "0px",
    backgroundColor: "transparent",
    border: "none",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: 600
  },
  candidateAvatar: {
    width: "38px",
    height: "38px",
    borderRadius: "0px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "14px",
    fontWeight: 600,
    boxShadow: "none",
    flexShrink: 0
  },
  candidateName: {
    margin: 0,
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: 600,
    textTransform: "uppercase"
  },
  infoText: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#475569",
    fontSize: "12.5px",
    lineHeight: 1.45,
    fontWeight: 500
  },
  onboardingBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "0px",
    fontSize: "12px",
    fontWeight: 600
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "0px",
    fontSize: "12px",
    fontWeight: 600
  },
  actionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap"
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "34px",
    padding: "0 12px",
    borderRadius: "0px",
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 600,
    border: "1px solid rgba(191, 219, 254, 1)",
    whiteSpace: "nowrap"
  },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  periodText: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 18px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    fontSize: "13px",
    fontWeight: 600,
    color: "#334155",
    marginBottom: "0",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  metricButton: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "3px",
    padding: "8px 14px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    color: "#334155",
    cursor: "pointer",
    textAlign: "left"
  },
  metricPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#0f172a"
  },
  metricSecondary: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em"
  },
  monthYearStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px"
  },
  monthYearPrimary: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#0f172a",
    textTransform: "capitalize",
    letterSpacing: "0.01em"
  },
  monthYearSecondary: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#94a3b8"
  },
  yearBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: "0px",
    backgroundColor: "#eff6ff",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    color: "#2563eb",
    fontSize: "11.5px",
    fontWeight: 600
  },
  primaryText: {
    fontSize: "13.5px",
    fontWeight: 600,
    color: "#0f172a"
  },
  mutedText: {
    fontSize: "12px",
    color: "#64748b",
    lineHeight: 1.5,
    marginTop: "4px",
    fontWeight: 600
  },
  moneyText: {
    fontSize: "13.5px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "4px"
  },
  invoiceNumberPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    color: "#0f172a",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "2px"
  },
  dangerTextButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: "0px",
    border: "1px solid #fca5a5",
    background: "#ffffff",
    color: "#dc2626",
    fontSize: "12px",
    fontWeight: 600,
    padding: "5px 10px",
    lineHeight: 1.2,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  reviewBadgeCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    borderRadius: "0px",
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: "10.5px",
    fontWeight: 600,
    padding: "1px 6px",
    lineHeight: 1.2,
    border: "1px solid #cbd5e1"
  },
  reviewPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    lineHeight: 1.2
  },
  viewAddPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid rgba(191, 219, 254, 1)",
    background: "#eff6ff",
    color: "#1d4ed8",
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(37, 99, 235, 0.04)"
  },
  amountBadgeNeutral: {
    display: "inline-flex",
    alignItems: "center",
    gap: "1px",
    padding: "5px 12px",
    borderRadius: "0px",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#0f172a",
    fontSize: "14px",
    fontWeight: 600
  },
  amountBadgeSuccess: {
    display: "inline-flex",
    alignItems: "center",
    gap: "1px",
    padding: "5px 12px",
    borderRadius: "0px",
    background: "#f0fdf4",
    border: "1px solid rgba(187, 247, 208, 1)",
    color: "#166534",
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "4px"
  },
  timesheetPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    width: "fit-content",
    padding: "9px 18px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    color: "#2563eb",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: "pointer",
    transition: "box-shadow 0.16s ease",
    whiteSpace: "nowrap"
  },
  docActionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    width: "fit-content",
    padding: "9px 18px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #cbd5e1",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  docActionDanger: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    width: "fit-content",
    padding: "9px 18px",
    borderRadius: "0px",
    background: "#ffffff",
    border: "1px solid #fca5a5",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    color: "#dc2626",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  invoiceStack: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "6px",
    maxWidth: "280px"
  },
  invoiceItem: {
    display: "inline-flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "6px"
  },
  invoiceRequestText: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#92400e",
    backgroundColor: "#fffbeb",
    border: "1px solid rgba(253, 230, 138, 1)",
    borderRadius: "0px",
    padding: "6px 12px",
    lineHeight: 1.2,
    whiteSpace: "nowrap"
  },
  statusPillBase: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "0px",
    padding: "5px 10px",
    fontSize: "11.5px",
    fontWeight: 600,
    border: "1px solid transparent"
  },
  successPill: {
    color: "#166534",
    backgroundColor: "#f0fdf4",
    borderColor: "rgba(187, 247, 208, 1)"
  },
  warningPill: {
    color: "#92400e",
    backgroundColor: "#fffbeb",
    borderColor: "rgba(253, 230, 138, 1)"
  },
  dangerPill: {
    color: "#991b1b",
    backgroundColor: "#fef2f2",
    borderColor: "rgba(254, 202, 202, 1)"
  },
  textButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    textDecoration: "none",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    maxWidth: "240px",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer"
  },
  docList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    alignItems: "flex-start"
  },
  docRow: {
    padding: 0,
    border: "none",
    background: "transparent",
    boxShadow: "none",
    display: "flex",
    alignItems: "center"
  },
  docLink: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    padding: 0,
    fontSize: "13px",
    fontWeight: 600,
    textAlign: "left",
    wordBreak: "break-word",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
    boxShadow: "none"
  },
  notesButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: "0px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    whiteSpace: "nowrap",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    lineHeight: 1.2
  },
  notesBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "18px",
    height: "18px",
    borderRadius: "0px",
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: 700,
    padding: "1px 5px",
    lineHeight: 1.2,
    border: "1px solid #cbd5e1",
    marginLeft: "4px"
  },
  paymentStack: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: "210px",
    maxWidth: "260px"
  },
  paymentCard: {
    borderRadius: "0px",
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    padding: "12px"
  },
  paymentButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    borderRadius: "0px",
    border: "1px solid rgba(187, 247, 208, 1)",
    background: "#f0fdf4",
    color: "#166534",
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    lineHeight: 1.2,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    whiteSpace: "nowrap"
  },
  paidAmountPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    borderRadius: "0px",
    border: "1px solid rgba(191, 219, 254, 1)",
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "7px 11px",
    fontSize: "12px",
    fontWeight: 600,
    marginTop: "8px"
  },
  paymentMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.5,
    marginTop: "8px"
  },
  paymentDocs: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    marginTop: "10px"
  },
  emptyState: {
    padding: "22px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "13px"
  }
};

const ROUTE_CONFIG = [
  { name: "Contract Management", url: "" },
  { name: "Invoices and Payments", url: "/PartnerInvoices" },
];

const formatNumericHours = (hours) => {
  const totalMinutes = Math.round((Number(hours) || 0) * 60);
  const finalHours = Math.floor(totalMinutes / 60);
  const finalMinutes = totalMinutes % 60;

  return `${finalHours}h ${finalMinutes}m`;
};

const formatProjectDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().substring(0, 10);
};

export default function CandidateInvoice() {

  const usrType = useMemo(() => localStorage.getItem("userType"), []);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const candidateObj = useMemo(() => location.state || null, [location.state]);
  const performerId = candidateObj?.performerid;
  const performerData = candidateObj?.performerData;

  const { data: timeLogsData = [], isLoading: isLoadingTimeLogs, isFetching: isFetchingTimeLogs, refetch: refetchTimeLogs } = useLoadTimeLogsQuery(performerId, { skip: !performerId });
  const { data: timeLogInvoicesData = [], isLoading: isLoadingInvoices, isFetching: isFetchingInvoices, refetch: refetchTimeLogInvoices } = useLoadTimeLogInvoiceQuery(performerId, { skip: !performerId });
  const { data: timeLogDurationData, isLoading: isLoadingDuration, isFetching: isFetchingDuration } = useLoadTimeLogDurationQuery(performerId, { skip: !performerId });
  const { data: allRatesData = [], isLoading: isLoadingRates, isFetching: isFetchingRates } = useLoadAllRatesQuery(performerId, { skip: !performerId });

  const isInitialLoading = isLoadingTimeLogs || isLoadingInvoices || isLoadingDuration || isLoadingRates;
  const isDataFetching = isFetchingTimeLogs || isFetchingInvoices || isFetchingDuration || isFetchingRates;
  const isDataLoading = isInitialLoading || (isDataFetching && timeLogsData.length === 0);

  const handleRefreshInvoices = useCallback(() => {
    if (refetchTimeLogs) refetchTimeLogs();
    if (refetchTimeLogInvoices) refetchTimeLogInvoices();
  }, [refetchTimeLogs, refetchTimeLogInvoices]);

  const [deleteInvoiceDoc, { isSuccess: deleteSuccess }] = useTimeLogInvDocDeleteReqMutation();

  const [filterYear, setFilterYear] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [timeLogObj, setTimeLogObj] = useState(null);
  const [noteShowHide, setNoteShowHide] = useState(false);
  const [showHide, setShowHide] = useState(false);
  const [payShowHide, setPayShowHide] = useState(false);
  const [invShowHide, setInvShowHide] = useState(false);
  const [poShowHide, setPoShowHide] = useState(false);

  const [handleDownloadFile] = useDocDownload();

  const years = useMemo(() => getAllYears(), []);
  const months = useMemo(() => getAllMonths(), []);

  const entType = useMemo(() =>
    getFieldData(performerData, "candidateonboardingform", "CandidateEntity") || "",
    [performerData]
  );

  const w9Type = useMemo(() =>
    getFieldData(performerData, "candidateonboardingform", "w9_Type") || "",
    [performerData]
  );

  const costBefenifit = useMemo(() =>
    getFieldData(performerData, "candidateonboardingform", "Cost_of_Benefit") || "",
    [performerData]
  );

  const rate = useMemo(() => {
    if (!candidateObj?.rates) return 0;
    return usrType === "recruiter"
      ? candidateObj.rates.recruiterRate
      : usrType === "subcontractor"
        ? candidateObj.rates.subcontractorRate
        : 0;
  }, [candidateObj, usrType]);

  const notesType = useMemo(() =>
    usrType === "recruiter" ? "TimeLogRecruiter" : "TimeLog",
    [usrType]
  );

  const timeLogs = useMemo(() => {
    let list = [...timeLogsData];
    list.sort((a, b) => {
      const yearA = parseInt(a.year, 10) || 0;
      const yearB = parseInt(b.year, 10) || 0;
      const monthA = parseInt(a.month, 10) || 0;
      const monthB = parseInt(b.month, 10) || 0;

      if (yearA !== yearB) return yearB - yearA;
      return monthB - monthA;
    });

    if (filterYear) {
      list = list.filter((x) => x.year === parseInt(filterYear, 10));
    }

    if (filterMonth) {
      list = list.filter((x) => x.month === parseInt(filterMonth, 10));
    }

    return list;
  }, [timeLogsData, filterYear, filterMonth]);

  const timeLogInvoices = useMemo(() => {
    if (timeLogInvoicesData.length > 0) {
      return timeLogInvoicesData.filter((x) => x.invoiceType === usrType);
    }
    return [];
  }, [timeLogInvoicesData, usrType]);

  const { projectStDt, currentStDt } = useMemo(() => {
    if (timeLogDurationData?.startDate) {
      const startDate = new Date(timeLogDurationData.startDate);
      startDate.setDate(startDate.getDate() + 1);
      return {
        projectStDt: startDate,
        currentStDt: timeLogDurationData.projectStartedDate,
      };
    }
    return { projectStDt: "", currentStDt: "" };
  }, [timeLogDurationData]);

  useEffect(() => {
    dispatch(setCurrentPageTitle("Candidate Invoice Detail"));
    dispatch(setCurrentRoute(ROUTE_CONFIG));
  }, [dispatch]);

  useEffect(() => {
    if (deleteSuccess) {
      getAlert("", "Request Sent Successfully.", "success");
    }
  }, [deleteSuccess]);

  const handlePayShowHide = useCallback((obj) => {
    setPayShowHide((prev) => !prev);
    setTimeLogObj(obj);
  }, []);

  const handleInvShowHide = useCallback((obj) => {
    if (obj === "save") {
      setInvShowHide(false);
      setTimeLogObj(null);
    } else {
      setInvShowHide((prev) => !prev);
      setTimeLogObj(obj);
    }
  }, []);

  const handlePoShowHide = useCallback(() => {
    setPoShowHide((prev) => !prev);
  }, []);

  const handleShowHide = useCallback((obj) => {
    setShowHide((prev) => !prev);
    setTimeLogObj(obj);
  }, []);

  const handleNotesComments = useCallback((type, obj) => {
    if (type === "save") {
      setTimeLogObj(null);
      setNoteShowHide(false);
    } else {
      setTimeLogObj(obj);
      setNoteShowHide((prev) => !prev);
    }
  }, []);

  const handleInvoiceDetailsFocus = useCallback(() => {
    const invoiceDetailsSection = document.getElementById("candidate-invoice-details");
    if (invoiceDetailsSection) {
      invoiceDetailsSection.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }, []);

  const getPendingPoCount = useCallback((obj) => (
    obj?.poTypes?.filter(
      (po) => po.type === usrType && po.partnerSign !== true && po.sendStatus === true
    ).length || 0
  ), [usrType]);

  const getTotalPoCount = useCallback((obj) => (
    obj?.poTypes?.filter((po) => po.type === usrType && po.sendStatus === true).length || 0
  ), [usrType]);

  const getPoCount = useCallback((obj) => {
    const pendingCount = getPendingPoCount(obj);
    const totalCount = pendingCount === 0 ? getTotalPoCount(obj) : pendingCount;
    const badgeClass = pendingCount === 0 ? "badge-success" : "badge-danger";

    return (
      <span className={`badge badge-pill ${badgeClass} ml-2`}>
        {totalCount}
      </span>
    );
  }, [getPendingPoCount, getTotalPoCount]);

  const getOnboardingBadgeStyle = useCallback((isComplete) => ({
    ...CANDIDATE_INVOICE_STYLES.onboardingBadge,
    backgroundColor: isComplete ? "#2dce898c" : "#ffd600",
    color: "#0f172a",
    border: `1px solid ${isComplete ? "#2dce89" : "#ffd600"}`
  }), []);

  const getApplicableRateDetails = useCallback((itemObj) => {
    const itemYear = parseInt(itemObj?.year, 10);
    const itemMonth = parseInt(itemObj?.month, 10);
    const rateField = usrType === "recruiter" ? "recruiterRate" : "subcontractorRate";
    const dateField = usrType === "recruiter" ? "recruiterEffectiveDate" : "subcontractorEffectiveDate";

    const matchedRate = Array.isArray(allRatesData)
      ? allRatesData.reduce((best, rateItem, index) => {
        if (!itemYear || !itemMonth) return best;

        const effMs = new Date(rateItem?.[dateField]).getTime();
        if (Number.isNaN(effMs)) return best;

        const effDt = new Date(effMs);
        const effYear = effDt.getFullYear();
        const effMonth = effDt.getMonth() + 1;
        const isApplicable =
          effYear < itemYear ||
          (effYear === itemYear && effMonth <= itemMonth);

        if (!isApplicable) return best;
        if (!best) return { rate: rateItem, index, effMs };

        if (effMs > best.effMs) return { rate: rateItem, index, effMs };
        if (effMs < best.effMs) return best;

        const addedMs = new Date(rateItem?.addedDateTime).getTime();
        const bestAddedMs = new Date(best.rate?.addedDateTime).getTime();
        const safeAddedMs = Number.isNaN(addedMs) ? -Infinity : addedMs;
        const safeBestAddedMs = Number.isNaN(bestAddedMs) ? -Infinity : bestAddedMs;

        if (safeAddedMs > safeBestAddedMs) return { rate: rateItem, index, effMs };
        if (safeAddedMs < safeBestAddedMs) return best;

        const idNum = Number(rateItem?.id);
        const bestIdNum = Number(best.rate?.id);
        const safeIdNum = Number.isFinite(idNum) ? idNum : -Infinity;
        const safeBestIdNum = Number.isFinite(bestIdNum) ? bestIdNum : -Infinity;

        if (safeIdNum > safeBestIdNum) return { rate: rateItem, index, effMs };
        if (safeIdNum < safeBestIdNum) return best;

        return index > best.index ? { rate: rateItem, index, effMs } : best;
      }, null)?.rate
      : null;

    const fallbackRate = parseFloat(rate || 0);
    const rateValue =
      matchedRate?.[rateField] !== null &&
        matchedRate?.[rateField] !== undefined &&
        matchedRate?.[rateField] !== ""
        ? parseFloat(matchedRate[rateField])
        : fallbackRate;

    const effectiveDate = matchedRate?.[dateField] || rate;

    return {
      rate: rateValue,
      effectiveDate,
    };
  }, [allRatesData, usrType, rate]);

  const getInvoiceAmount = useCallback((itemObj) => {
    const totHrs = getTotalHours(itemObj?.workingHrs || 0);
    const { rate: applicableRate } = getApplicableRateDetails(itemObj);
    let invAmt = totHrs * applicableRate;

    if (entType !== "W2 Basis") {
      return parseFloat(invAmt || 0).toFixed(2);
    }

    const taxRate = (getFieldData(performerData, "candidateonboardingform", "Tax") || 0) / 100;
    invAmt = invAmt + invAmt * taxRate;

    return parseFloat(invAmt || 0).toFixed(2);
  }, [getApplicableRateDetails, entType, performerData]);

  const getFinalAmout = useCallback((obj) => {
    let fAmt = parseFloat(getInvoiceAmount(obj) || 0);

    const invItems = timeLogInvoices.filter(
      (x) => x.timeLogId === obj.id
    );

    if (invItems.length === 0) {
      return fAmt.toFixed(2);
    }

    const itemObj = invItems[0];

    const adjMinus = parseFloat(itemObj?.adjustmentMinues || 0);
    const adjPlus = parseFloat(itemObj?.adjustmentPlus || 0);
    const adp = parseFloat(itemObj?.adpDeduction || 0);

    fAmt = fAmt - adjMinus + adjPlus - adp;

    return fAmt.toFixed(2);
  }, [getInvoiceAmount, timeLogInvoices]);

  const getMonthlyPeriod = useCallback((obj) => {
    if (!obj?.workingHrs) return null;

    let wt;
    try {
      wt = JSON.parse(obj.workingHrs);
    } catch (e) {
      return null;
    }

    if (!Array.isArray(wt) || wt.length === 0) return null;

    const sortWt = [...wt].sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );

    const first = sortWt[0];
    const last = sortWt[sortWt.length - 1];

    const lastDate =
      last.type === "m"
        ? getMonthLastDay(last.start)
        : last.start;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "5px", textAlign: "left" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>
          <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>FROM</span>
          <Moment format="MM/DD/YYYY">{first.start}</Moment>
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 600, color: "#0f172a" }}>
          <span style={{ color: "#94a3b8", fontWeight: 600, fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.04em" }}>TO</span>
          <Moment format="MM/DD/YYYY">{lastDate}</Moment>
        </span>
      </div>
    );
  }, []);

  const getShortDocumentName = useCallback((name = "", maxLength = 15) => {
    if (!name) return "Document";
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  }, []);

  const getInvoiceDeduction = useCallback((itemObj) => {
    const tmLogs = timeLogInvoices.filter(
      (x) =>
        x.timeLogId === itemObj.id
        && x.adpDeduction !== null
    );
    if (tmLogs.length === 0) {
      return "";
    }
    return <>
      {tmLogs.map((inv) => (
        <React.Fragment key={inv.id}>
          <div style={CANDIDATE_INVOICE_STYLES.moneyText}>{inv.adpDeduction}</div>
          <br />
          {inv?.adpDocList?.map(
            (doc) => (
              <button
                key={doc.id || doc.documentName}
                className="btn btn-sm"
                style={CANDIDATE_INVOICE_STYLES.textButton}
                title={doc.documentName}
                onClick={() =>
                  handleDownloadFile(
                    doc.invDoc
                  )
                }
              >
                <i className="fa fa-paperclip" aria-hidden="true"></i>
                {getShortDocumentName(doc.documentName)}
              </button>
            )
          )}
          {inv?.adpDocList?.length > 0 && <br />}
          <div style={CANDIDATE_INVOICE_STYLES.mutedText}>
            <Moment format="MMM Do YYYY, h:mm a">
              {inv.adpDateTime}
            </Moment>
          </div>
        </React.Fragment>
      ))}
    </>;
  }, [timeLogInvoices, handleDownloadFile, getShortDocumentName]);

  const handleDeleteInvoiceDoc = useCallback(async (id) => {
    const result = await getConfirmAlert("", "Are you sure you want to send request to delete this document?", "error");
    if (result.isConfirmed) {
      deleteInvoiceDoc(id);
    }
  }, [deleteInvoiceDoc]);

  const getGeneratedInvoice = useCallback((itemObj) => {
    const tmStatus = timeLogInvoices.filter((x) =>
      x.timeLogId === itemObj.id && x.status !== null);

    const tmLogs = timeLogInvoices.filter(
      (x) => x.timeLogId === itemObj.id && x.invDoc !== null);
    if (tmLogs.length === 0) {
      return (
        <div style={CANDIDATE_INVOICE_STYLES.invoiceStack}>
          <button
            className="btn btn-sm"
            style={CANDIDATE_INVOICE_STYLES.docActionButton}
            onClick={() =>
              handleInvShowHide(itemObj)
            }
            disabled={tmStatus[0]?.status === false}
          >
            <i className="fa fa-upload" aria-hidden="true" style={{ color: "#2563eb" }}></i>
            Upload / Generate
          </button>
        </div>
      );
    }

    return <div style={CANDIDATE_INVOICE_STYLES.invoiceStack}>
      {tmLogs.map((inv) => (
        <div key={inv.id} style={CANDIDATE_INVOICE_STYLES.invoiceItem}>
          <span style={CANDIDATE_INVOICE_STYLES.invoiceNumberPill}>
            Inv: {inv.invoiceNumber}
          </span>

          <button
            type="button"
            className="doc-link"
            style={CANDIDATE_INVOICE_STYLES.docLink}
            title={inv.invDoc.documentName}
            onClick={() => handleDownloadFile(inv.invDoc)}
          >
            <i className="fa fa-file-pdf-o" aria-hidden="true" style={{ color: "#dc2626", marginRight: "6px" }}></i>
            {inv.invDoc.documentName}
          </button>
          {!tmStatus[0]?.status && (
            inv.notes === "delete" ? (
              <span style={CANDIDATE_INVOICE_STYLES.invoiceRequestText}>
                <i className="fa fa-info-circle" aria-hidden="true"></i>
                Request Sent to Delete
              </span>
            ) : (
              <button
                type="button"
                className="btn btn-sm"
                style={{ ...CANDIDATE_INVOICE_STYLES.docActionDanger, width: "auto" }}
                title="Delete Request Invoice Document"
                onClick={() => handleDeleteInvoiceDoc(inv.id)}
              >
                <i className="fa fa-trash" aria-hidden="true"></i> Send Delete Request
              </button>
            )
          )}

        </div>
      ))}
    </div>;
  }, [timeLogInvoices, handleInvShowHide, handleDownloadFile, handleDeleteInvoiceDoc]);

  const getInvoiceStatus = useCallback((itemObj) => {
    const tmLogs = timeLogInvoices.filter((x) =>
      x.timeLogId === itemObj.id && x.status !== null);

    if (tmLogs.length > 0) {
      return <>
        {tmLogs.map((inv) => (
          <React.Fragment key={inv.id}>
            {inv.status === true && (
              <span style={{ ...CANDIDATE_INVOICE_STYLES.statusPillBase, ...CANDIDATE_INVOICE_STYLES.successPill }}>
                <i className="fa fa-check" aria-hidden="true"></i> Approved
              </span>
            )}
            {inv.status === false && (
              <span style={{ ...CANDIDATE_INVOICE_STYLES.statusPillBase, ...CANDIDATE_INVOICE_STYLES.dangerPill }}>
                <i className="fa fa-times" aria-hidden="true"></i> Rejected
              </span>
            )}
            <br />
            <div style={CANDIDATE_INVOICE_STYLES.mutedText}>
              <Moment format="MMM Do YYYY">
                {inv.reviewDateTime}
              </Moment>
            </div>
          </React.Fragment>
        ))}
      </>;
    }

    return "";
  }, [timeLogInvoices]);

  const getPaymentStatus = useCallback((itemObj) => {
    const tmLogs = timeLogInvoices.filter(
      (x) => x.timeLogId === itemObj.id &&
        x.paymentMode !== null
    );

    if (tmLogs.length > 0) {
      return (
        <div style={CANDIDATE_INVOICE_STYLES.paymentStack}>
          {tmLogs.map((pay) => (
            <div key={pay.id} style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
              <button
                type="button"
                className="btn btn-link p-0"
                style={{
                  color: "#166534",
                  fontWeight: 600,
                  fontSize: "13px",
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "2px",
                  textDecoration: "none",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left"
                }}
                onClick={() => handlePayShowHide(pay)}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <i className="fa fa-check-circle" aria-hidden="true" style={{ color: "#16a34a", fontSize: "13px" }}></i>
                  Paid By IPCS
                </span>
                <span style={{ color: "#166534", fontSize: "12.5px", fontWeight: 600, paddingLeft: "19px" }}>
                  (${pay.paidAmount || "0.00"})
                </span>
              </button>
              {pay.docList?.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "2px" }}>
                  {pay.docList.map((doc) => (
                    <button
                      key={doc.id || doc.documentName}
                      type="button"
                      className="doc-link"
                      style={CANDIDATE_INVOICE_STYLES.docLink}
                      title={doc.documentName}
                      onClick={() => handleDownloadFile(doc)}
                    >
                      <i className={`fa ${getDocumentIcon(doc)}`} aria-hidden="true" style={{ ...getDocumentIconStyle(doc), marginRight: "6px" }}></i>
                      {doc.documentName}
                    </button>
                  ))}
                </div>
              )}
              {(pay.paymentDate || pay.addedDatetime) && (
                <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 500, marginTop: "2px" }}>
                  <Moment format="MMM Do YYYY">
                    {pay.paymentDate || pay.addedDatetime}
                  </Moment>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    }
    return "";
  }, [timeLogInvoices, handlePayShowHide, handleDownloadFile]);

  const item = candidateObj || "";

  const showInvoiceColumn = useMemo(() =>
    entType === "C2C Basis" || usrType === "recruiter",
    [entType, usrType]
  );

  const emptyInvoiceColSpan = useMemo(() =>
    10 +
    (w9Type === "W2 Hourly with Benefits" ? 1 : 0) +
    (entType === "W2 Basis" && usrType !== "recruiter" ? 1 : 0) +
    (showInvoiceColumn ? 1 : 0),
    [w9Type, entType, usrType, showInvoiceColumn]
  );

  const totalInvoiceHours = useMemo(() =>
    timeLogs.reduce(
      (sum, log) => sum + (Number(getTotalHours(log?.workingHrs || 0)) || 0),
      0
    ),
    [timeLogs]
  );

  const totalInvoiceDays = useMemo(() =>
    timeLogs.reduce(
      (sum, log) => sum + (Number(getTotalDays(log?.workingHrs || 0)) || 0),
      0
    ),
    [timeLogs]
  );

  const reviewedInvoices = useMemo(() =>
    timeLogs.filter((log) =>
      timeLogInvoices.some((invoice) => invoice.timeLogId === log.id && invoice.status !== null)
    ).length,
    [timeLogs, timeLogInvoices]
  );

  const paidInvoices = useMemo(() =>
    timeLogs.filter((log) =>
      timeLogInvoices.some((invoice) => invoice.timeLogId === log.id && Number(invoice.paidAmount) > 0)
    ).length,
    [timeLogs, timeLogInvoices]
  );

  const totalFinalPay = useMemo(() =>
    timeLogs.reduce(
      (sum, log) => sum + (Number(getFinalAmout(log)) || 0),
      0
    ),
    [timeLogs, getFinalAmout]
  );

  const projectDateLabel = useMemo(() =>
    formatProjectDate(projectStDt) || formatProjectDate(currentStDt) || "Not available",
    [projectStDt, currentStDt]
  );

  return (
    <div className="w-100 mt-2" style={{ width: "100%" }}>

      <style>{`
        .candidate-invoice-table tbody tr:hover td {
          background-color: #f8fbff !important;
        }

        .doc-link {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
          color: #2563eb !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          text-align: left !important;
          display: inline-flex !important;
          align-items: center !important;
          cursor: pointer !important;
          text-decoration: none !important;
          box-shadow: none !important;
        }

        .doc-link:hover {
          text-decoration: underline !important;
          color: #1d4ed8 !important;
        }

        @media (max-width: 991.98px) {
          .candidate-invoice-table {
            min-width: 1100px;
          }
        }
      `}</style>

      <div style={CANDIDATE_INVOICE_STYLES.pageActions}>
        <button
          className="btn btn-sm"
          style={CANDIDATE_INVOICE_STYLES.backButton}
          onClick={() => navigate(-1)}
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i> Back
        </button>
      </div>
      <div>
        <div style={CANDIDATE_INVOICE_STYLES.summaryTableWrap}>
          <div className="table-responsive">
            <table className="table align-items-center table-hover mb-0 candidate-invoice-table" style={CANDIDATE_INVOICE_STYLES.candidateSummaryTable}>
              <thead>
                <tr>
                  <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Candidate #<br />IPCS ID</th>
                  <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Candidate Details</th>
                  <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Agency</th>
                  <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Duration<br />(Start-End)</th>
                  <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Onboarding<br />Status</th>
                  <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Purchase Order</th>
                </tr>
              </thead>
              <tbody>
                {item !== "" &&
                  <tr>
                    <td style={CANDIDATE_INVOICE_STYLES.candidateSummaryCell}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "flex-start" }}>
                        <span style={CANDIDATE_INVOICE_STYLES.idPill}>
                          <span style={{ color: "#94a3b8", fontWeight: 600 }}>#</span> {item.performerid}
                        </span>
                        <span style={CANDIDATE_INVOICE_STYLES.secondaryPill}>
                          IPCS ID: {item.maxloans || "N/A"}
                        </span>
                      </div>
                    </td>
                    <td style={CANDIDATE_INVOICE_STYLES.candidateSummaryCell}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                        <div style={CANDIDATE_INVOICE_STYLES.candidateAvatar}>
                          {getInitials(item.performername, item.lastName)}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                          <p style={CANDIDATE_INVOICE_STYLES.candidateName}>
                            {item.performername} {item.lastName}
                          </p>
                          {item.performeremail && (
                            <span style={CANDIDATE_INVOICE_STYLES.infoText}>
                              {item.performeremail}
                            </span>
                          )}
                          {item.contactNo && (
                            <span style={CANDIDATE_INVOICE_STYLES.infoText}>
                              {getFormatedNumber(item.contactNo)}
                            </span>
                          )}
                          {getFieldData(item.performerData, "projectengagement", "PositionTitle") && (
                            <span style={{ ...CANDIDATE_INVOICE_STYLES.infoText, color: "#475569", fontWeight: 500, marginTop: "2px" }}>
                              {getFieldData(item.performerData, "projectengagement", "PositionTitle")}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={CANDIDATE_INVOICE_STYLES.candidateSummaryCell}>
                      <span style={{ ...CANDIDATE_INVOICE_STYLES.infoText, fontWeight: 500, color: "#475569" }}>
                        {getFieldData(item.performerData, "projectengagement", "AgencyName") || "N/A"}
                      </span>
                    </td>
                    <td style={CANDIDATE_INVOICE_STYLES.candidateSummaryCell}>
                      {item.duration ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                          <span style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "3px 10px",
                            borderRadius: "0px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            color: "#0f172a",
                            fontSize: "11.5px",
                            fontWeight: 600,
                            width: "fit-content",
                            marginBottom: "4px"
                          }}>
                            {item.duration.duration} Months
                          </span>
                          <span style={CANDIDATE_INVOICE_STYLES.infoText}>
                            Start: <Moment format="MMM Do YYYY">{item.duration.startDate}</Moment>
                          </span>
                          <span style={CANDIDATE_INVOICE_STYLES.infoText}>
                            End: <Moment format="MMM Do YYYY">{item.duration.endDate}</Moment>
                          </span>
                        </div>
                      ) : (
                        <span style={{ ...CANDIDATE_INVOICE_STYLES.infoText, color: "#94a3b8" }}>N/A</span>
                      )}
                    </td>
                    <td style={CANDIDATE_INVOICE_STYLES.candidateSummaryCell}>
                      <span style={getOnboardingBadgeStyle(item.appSubmit)}>
                        {item.appSubmit ? "Complete" : "Pending"}
                      </span>
                    </td>
                    <td style={CANDIDATE_INVOICE_STYLES.candidateSummaryCell}>
                      <button
                        className="btn btn-sm"
                        onClick={() => handlePoShowHide(item)}
                        style={CANDIDATE_INVOICE_STYLES.actionButton}
                      >
                        <i className="fa fa-pencil-square-o" aria-hidden="true"></i>
                        View and eSign
                        {getPoCount(item)}
                      </button>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div id="candidate-invoice-details" style={CANDIDATE_INVOICE_STYLES.card}>
          <div style={CANDIDATE_INVOICE_STYLES.cardHeader}>
            <div style={CANDIDATE_INVOICE_STYLES.headerRow}>
              <div style={CANDIDATE_INVOICE_STYLES.titleWrap}>
                <div style={CANDIDATE_INVOICE_STYLES.titleIcon}>
                  <i className="fa fa-file-text-o" aria-hidden="true"></i>
                </div>
                <div>
                  <h1 style={CANDIDATE_INVOICE_STYLES.title}>Invoice Details</h1>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm"
                style={CANDIDATE_INVOICE_STYLES.refreshButton}
                onClick={handleRefreshInvoices}
                disabled={isDataFetching}
                title="Refresh Invoice Details"
              >
                <i className={`fa fa-refresh ${isDataFetching ? "fa-spin" : ""}`} aria-hidden="true"></i>{" "}
                {isDataFetching ? "Fetching..." : "Refresh"}
              </button>
            </div>
          </div>

          {isDataFetching && timeLogs.length > 0 && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 18px",
              background: "#eff6ff",
              borderTop: "1px solid #bfdbfe",
              borderBottom: "1px solid #bfdbfe",
              color: "#1e40af",
              fontSize: "12px",
              fontWeight: 600
            }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                <i className="fa fa-circle-o-notch fa-spin" style={{ color: "#2563eb", fontSize: "13px" }}></i>
                <span>Fetching updated invoice details...</span>
              </div>
              <span style={{ fontSize: "11px", color: "#3b82f6", fontWeight: 500 }}>Updating</span>
            </div>
          )}

          <div style={CANDIDATE_INVOICE_STYLES.statsRow}>
            <div style={CANDIDATE_INVOICE_STYLES.statCard}>
              <div style={CANDIDATE_INVOICE_STYLES.statIcon}>
                <i className="fa fa-calendar-o" aria-hidden="true"></i>
              </div>
              <div style={CANDIDATE_INVOICE_STYLES.statContent}>
                <div style={CANDIDATE_INVOICE_STYLES.statLabel}>Invoice Periods</div>
                <div style={CANDIDATE_INVOICE_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : timeLogs.length}
                </div>
              </div>
            </div>
            <div style={CANDIDATE_INVOICE_STYLES.statCard}>
              <div style={CANDIDATE_INVOICE_STYLES.statIcon}>
                <i className="fa fa-clock-o" aria-hidden="true"></i>
              </div>
              <div style={CANDIDATE_INVOICE_STYLES.statContent}>
                <div style={CANDIDATE_INVOICE_STYLES.statLabel}>Total Hours</div>
                <div style={CANDIDATE_INVOICE_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : formatNumericHours(totalInvoiceHours)}
                </div>
              </div>
            </div>
            <div style={CANDIDATE_INVOICE_STYLES.statCard}>
              <div style={CANDIDATE_INVOICE_STYLES.statIcon}>
                <i className="fa fa-check-circle-o" aria-hidden="true"></i>
              </div>
              <div style={CANDIDATE_INVOICE_STYLES.statContent}>
                <div style={CANDIDATE_INVOICE_STYLES.statLabel}>Reviewed / Paid</div>
                <div style={CANDIDATE_INVOICE_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : <>{reviewedInvoices} / {paidInvoices}</>}
                </div>
              </div>
            </div>
            <div style={CANDIDATE_INVOICE_STYLES.statCard}>
              <div style={CANDIDATE_INVOICE_STYLES.statIcon}>
                <i className="fa fa-usd" aria-hidden="true"></i>
              </div>
              <div style={CANDIDATE_INVOICE_STYLES.statContent}>
                <div style={CANDIDATE_INVOICE_STYLES.statLabel}>Final Pay</div>
                <div style={CANDIDATE_INVOICE_STYLES.statValue}>
                  {isDataLoading ? <i className="fa fa-circle-o-notch fa-spin" style={{ fontSize: "16px", color: "#64748b" }}></i> : totalFinalPay.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
          <div style={CANDIDATE_INVOICE_STYLES.cardBody}>
            <div style={CANDIDATE_INVOICE_STYLES.utilityRow}>
              <div style={CANDIDATE_INVOICE_STYLES.utilityPanel}>
                <div style={CANDIDATE_INVOICE_STYLES.utilityPanelTop}>
                  <span style={CANDIDATE_INVOICE_STYLES.utilityIcon}>
                    <i className="fa fa-calendar" aria-hidden="true"></i>
                  </span>
                  <div style={CANDIDATE_INVOICE_STYLES.utilityCopy}>
                    <div style={CANDIDATE_INVOICE_STYLES.utilityLabel}>Date Started On The Project</div>
                    <div style={CANDIDATE_INVOICE_STYLES.utilityValue}>{projectDateLabel}</div>
                  </div>
                </div>
                <div style={CANDIDATE_INVOICE_STYLES.utilityFilters}>
                  <div style={CANDIDATE_INVOICE_STYLES.utilityFilterField}>
                    <select
                      name="filterYear"
                      id="year"
                      className="form-control"
                      style={CANDIDATE_INVOICE_STYLES.inputShell}
                      value={filterYear ? filterYear.toString() : ""}
                      onChange={(e) => setFilterYear(e.target.value)}
                    >
                      <option value="">Year</option>
                      {years.map((y) => (
                        <option key={y.name} value={y.name.toString()}>{y.name}</option>
                      ))}
                    </select>
                  </div>
                  <div style={CANDIDATE_INVOICE_STYLES.utilityFilterField}>
                    <select
                      name="filterMonth"
                      id="month"
                      className="form-control"
                      style={CANDIDATE_INVOICE_STYLES.inputShell}
                      value={filterMonth ? filterMonth.toString() : ""}
                      onChange={(e) => setFilterMonth(e.target.value)}
                    >
                      <option value="">Month</option>
                      {months.map((m) => (
                        <option key={m.id} value={m.id.toString()}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div style={CANDIDATE_INVOICE_STYLES.tableWrap}>
              <div className="table-responsive">
                <table className="table align-items-center table-hover mb-0 candidate-invoice-table" style={CANDIDATE_INVOICE_STYLES.table}>
                  <thead>
                    <tr>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Month<span style={CANDIDATE_INVOICE_STYLES.tableHeadSubtext}>Year</span></th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Period</th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Hours<span style={CANDIDATE_INVOICE_STYLES.tableHeadSubtext}>Days</span></th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Approved<span style={CANDIDATE_INVOICE_STYLES.tableHeadSubtext}>Timesheets</span></th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Status</th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Rate/$Hr</th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Invoice<span style={CANDIDATE_INVOICE_STYLES.tableHeadSubtext}>Amount ($)</span></th>
                      {w9Type === "W2 Hourly with Benefits" && (
                        <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Cost of benefit ($)</th>
                      )}
                      {entType === "W2 Basis" && usrType !== "recruiter" && (
                        <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>ADP Deduction ($)</th>
                      )}
                      {showInvoiceColumn && (
                        <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Invoice</th>
                      )}
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Notes</th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Net Pay<span style={CANDIDATE_INVOICE_STYLES.tableHeadSubtext}>Amount ($)</span></th>
                      <th style={CANDIDATE_INVOICE_STYLES.tableHeadCell}>Payment</th>
                      {/* <th>Due Amount ($)</th> */}
                    </tr>
                  </thead>
                  <tbody style={{ opacity: isDataFetching && timeLogs.length > 0 ? 0.7 : 1, transition: "opacity 0.2s ease" }}>
                    {isDataLoading ? (
                      <tr>
                        <td colSpan={emptyInvoiceColSpan} style={{ padding: "48px 20px", textAlign: "center", backgroundColor: "#ffffff" }}>
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "40px", height: "40px", background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", color: "#000000", fontSize: "16px" }}>
                              <i className="fa fa-circle-o-notch fa-spin"></i>
                            </div>
                            <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                              {isDataFetching ? "Fetching invoices..." : "Loading invoices..."}
                            </div>
                            <div style={{ fontSize: "12px", fontWeight: 500, color: "#64748b" }}>
                              Please wait while we retrieve invoice details.
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : timeLogs.length === 0 ? (
                      <tr>
                        <td colSpan={emptyInvoiceColSpan} style={{ padding: "48px 20px", textAlign: "center", background: "#ffffff" }}>
                          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: "12px", maxWidth: "380px", margin: "0 auto" }}>
                            <div style={{ width: "48px", height: "48px", borderRadius: "14px", background: "#eff6ff", color: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", border: "1px solid rgba(191, 219, 254, 0.9)" }}>
                              <i className="fa fa-file-text-o" aria-hidden="true"></i>
                            </div>
                            <div>
                              <div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a", marginBottom: "4px" }}>No Invoices Found</div>
                              <div style={{ fontSize: "13px", fontWeight: 600, color: "#64748b", lineHeight: 1.5 }}>There are no invoice records found for the selected filters. Select another period or click Refresh.</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      timeLogs.map((item) => (
                      <tr key={item.id}>
                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <span style={CANDIDATE_INVOICE_STYLES.monthYearStack}>
                            <span style={CANDIDATE_INVOICE_STYLES.monthYearPrimary}>
                              {getMonthName(item.month)}
                            </span>
                            <span style={CANDIDATE_INVOICE_STYLES.monthYearSecondary}>
                              {item.year}
                            </span>
                          </span>
                        </td>
                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={CANDIDATE_INVOICE_STYLES.periodText}
                            onClick={() => handleShowHide(item)}
                          >
                            {getMonthlyPeriod(item)}
                          </button>
                        </td>

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={CANDIDATE_INVOICE_STYLES.metricButton}
                            onClick={() => handleShowHide(item)}
                          >
                            <span style={CANDIDATE_INVOICE_STYLES.metricPrimary}>
                              {formatTotalHours(item?.workingHrs)}
                            </span>
                            <span style={CANDIDATE_INVOICE_STYLES.metricSecondary}>
                              {`${getTotalDays(item.workingHrs)} ${getTotalDays(item.workingHrs) === 1 || getTotalDays(item.workingHrs) === 0 ? "DAY" : "DAYS"}`}
                            </span>
                          </button>
                        </td>

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          {item.docList?.length > 0 ? (
                            <ul style={CANDIDATE_INVOICE_STYLES.docList}>
                              {item.docList.map((doc) => (
                                <li key={doc.id || doc.documentName} style={CANDIDATE_INVOICE_STYLES.docRow}>
                                  <button
                                    type="button"
                                    className="doc-link"
                                    style={CANDIDATE_INVOICE_STYLES.docLink}
                                    title={doc.documentName}
                                    onClick={() => handleDownloadFile(doc)}
                                  >
                                    <i className={`fa ${getDocumentIcon(doc)}`} aria-hidden="true" style={{ ...getDocumentIconStyle(doc), marginRight: "6px" }}></i>
                                    {doc.documentName}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 600 }}>—</span>
                          )}
                        </td>

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <button
                            className="btn btn-sm"
                            style={CANDIDATE_INVOICE_STYLES.reviewPill}
                            onClick={() => handleNotesComments("", item)}
                          ><i className="fa fa-clock-o" aria-hidden="true" style={{ fontSize: "12px", margin: 0, padding: 0 }}></i><span>Review</span></button>
                        </td>

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          {(() => {
                            const { rate, effectiveDate } = getApplicableRateDetails(item);
                            return (
                              <>
                                <div style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a", marginBottom: "2px" }}>
                                  <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 600 }}>$</span>{rate}
                                  <span style={{ fontSize: "11px", fontWeight: 600, color: "#64748b", marginLeft: "3px" }}>/ HR</span>
                                </div>
                                {effectiveDate && (
                                  <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "11px", color: "#64748b", fontWeight: 600 }}>
                                    Eff:{" "}
                                    <Moment format="MMM Do YYYY">
                                      {effectiveDate}
                                    </Moment>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                            ${getInvoiceAmount(item)}
                          </span>
                        </td>

                        {w9Type === "W2 Hourly with Benefits" && (
                          <td style={CANDIDATE_INVOICE_STYLES.cell}>{costBefenifit}</td>
                        )}
                        {entType === "W2 Basis" && usrType !== "recruiter" && (
                          <td style={CANDIDATE_INVOICE_STYLES.cell}>
                            {getInvoiceDeduction(item)}
                          </td>
                        )}
                        {showInvoiceColumn && (
                          <td style={CANDIDATE_INVOICE_STYLES.cell}>
                            {getGeneratedInvoice(item)}
                          </td>
                        )}

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <button
                            className="btn btn-sm"
                            style={CANDIDATE_INVOICE_STYLES.notesButton}
                            onClick={() => handleNotesComments("", item)}
                          ><i className="fa fa-commenting-o" aria-hidden="true" style={{ fontSize: "12px", margin: 0, padding: 0 }}></i><span>View</span><span style={CANDIDATE_INVOICE_STYLES.notesBadge}>{item?.allComments.filter(x => x.commentType === notesType).length}</span></button>
                        </td>

                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          <div>
                            <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>
                              ${getFinalAmout(item)}
                            </span>
                          </div>
                          {getInvoiceStatus(item)}
                        </td>
                        <td style={CANDIDATE_INVOICE_STYLES.cell}>
                          {getPaymentStatus(item)}
                        </td>
                      </tr>
                    ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CandidatePay
        payShowHide={payShowHide}
        handlePayShowHide={handlePayShowHide}
        candidateObj={item}
        selectedObj={timeLogObj}
        timeLogsData={timeLogs}
        grossPay={
          (() => {
            const currentLog = timeLogs.find((log) => log.id === timeLogObj?.timeLogId);
            return currentLog ? getInvoiceAmount(currentLog) : 0;
          })()
        }
        netPay={
          (() => {
            const currentLog = timeLogs.find((log) => log.id === timeLogObj?.timeLogId);
            return currentLog ? getFinalAmout(currentLog) : 0;
          })()
        }
      ></CandidatePay>

      <AddNewTimeLog
        showHide={showHide}
        handleShowHide={handleShowHide}
        selectedObj={timeLogObj}
        candidateObj={item}
        viewType="invoice"
        allTimeLogs={timeLogs}
        projectStartDate={projectStDt}
      ></AddNewTimeLog>

      <UploadInvoice
        invShowHide={invShowHide}
        handleInvShowHide={handleInvShowHide}
        candidateObj={item}
        selectedObj={timeLogObj}
        timeLogInvoices={timeLogInvoices}
        allRates={allRatesData}
        entType={entType}
      ></UploadInvoice>
      <PurchaseOrders
        poShowHide={poShowHide}
        handlePoShowHide={handlePoShowHide}
        candidateObj={item}
      ></PurchaseOrders>
      {noteShowHide &&
        <AddEditNotes
          noteShowHide={noteShowHide}
          handleNoteShowHide={handleNotesComments}
          notesType={notesType}
          candidateObj={item}
          timeLogObj={timeLogObj}
        ></AddEditNotes>}
    </div>
  );
}
